import React, { useState, useEffect } from "react";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import EditIcon from "@mui/icons-material/Edit";
import { db } from "../../firebase";
import { ref, set, onValue, update } from "firebase/database";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import AddIcon from "@mui/icons-material/Add";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import NoAccountsIcon from "@mui/icons-material/NoAccounts";
import HomeIcon from "@mui/icons-material/Home";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import Groups2Icon from "@mui/icons-material/Groups2";
import { HiSwitchHorizontal } from "react-icons/hi";
import { MdPendingActions } from "react-icons/md";

const Sidebar = ({
  selectedUser,
  setSelectedUser,
  setShowAll,
  setShowSales,
  setShowBookings,
  topbarHeight = 64,
  setShowInactive,
  todayBookingCount = 0,
  setShowDaily,
  setShowEmployees,
  setShowAttendanceUI,
  setShowAttendanceReportUI,
  setShowPaymentRequests,
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [nextId, setNextId] = useState("");
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [editUser, setEditUser] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showUpiModal, setShowUpiModal] = useState(false);
  const [upiId, setUpiId] = useState("");
  const [savingUpi, setSavingUpi] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const [newUser, setNewUser] = useState({
    name: "",
    contact: "",
    address: "",
    capsulePrice: "",
    jarPrice: "",
    password: "",
    joiningDate: new Date().toISOString().slice(0, 10),
    customerType: "Regular",
  });

  const saveUpiId = async () => {
    if (!upiId.trim()) {
      alert("Please enter UPI ID");
      return;
    }

    try {
      setSavingUpi(true);

      await set(ref(db, "settings/upi"), {
        upiId: upiId.trim(),
        updatedAt: new Date().toISOString(),
      });

      alert("✅ UPI ID updated successfully");
      closeUpiModal();
    } catch (err) {
      console.error(err);
      alert("❌ Failed to update UPI");
    } finally {
      setSavingUpi(false);
    }
  };

  const closeUpiModal = () => {
    setIsClosing(true);

    setTimeout(() => {
      setShowUpiModal(false);
      setIsClosing(false);
      setUpiId("");
    }, 250);
  };

  const inactiveUsers = users.filter((u) => u.status === "inactive");

  // 📱 Detect mobile
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setSidebarOpen(!mobile);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 🎯 Generate random ID
  useEffect(() => {
    const id = "U" + Math.floor(1000 + Math.random() * 9000);
    setNextId(id);
  }, [showModal]);

  // 🔁 Fetch all users from Firebase
  useEffect(() => {
    const usersRef = ref(db, "users/");
    return onValue(usersRef, (snapshot) => {
      const data = snapshot.val();
      const allUsers = data
        ? Object.keys(data).map((key) => ({
            firebaseKey: key,
            ...data[key],
          }))
        : [];

      setUsers(allUsers.filter((u) => u.status !== "inactive"));
    });
  }, []);

  // ➕ Add new user
  const handleAddUser = async () => {
    if (!newUser.name.trim() || !newUser.contact.trim()) {
      alert("Please fill all required fields!");
      return;
    }

    setLoading(true);
    try {
      const safeKey = newUser.name.replace(/\s+/g, "_");
      await set(ref(db, "users/" + safeKey), {
        ...newUser,
        userId: nextId,
        createdAt: new Date().toISOString(),
        status: "active",
      });
      alert("✅ Customer added successfully!");
      setShowModal(false);
      setNewUser({
        name: "",
        contact: "",
        address: "",
        capsulePrice: "",
        jarPrice: "",
      });
    } catch (error) {
      console.error("Error adding user:", error);
      alert("❌ Error adding user");
    } finally {
      setLoading(false);
    }
  };

  // ✏️ Update user
  const handleEditSave = async () => {
    if (!editUser || !editUser.firebaseKey) return;
    setLoading(true);
    try {
      const userRef = ref(db, "users/" + editUser.firebaseKey);
      await update(userRef, {
        name: editUser.name,
        contact: editUser.contact,
        address: editUser.address,
        capsulePrice: editUser.capsulePrice,
        jarPrice: editUser.jarPrice,
      });
      alert("✅ Customer updated successfully!");
      setShowEditModal(false);
    } catch (error) {
      console.error("Update failed:", error);
      alert("❌ Update failed!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleShowBookings = () => {
      setShowBookings(true);
      setShowAll(false);
      setShowSales(false);
      setSelectedUser("");
    };
    window.addEventListener("showBookingsTable", handleShowBookings);
    return () =>
      window.removeEventListener("showBookingsTable", handleShowBookings);
  });

  // 🗑️ Deactivate user
  const handleDeleteUser = async (user) => {
    if (!user.firebaseKey) return;

    const confirmDelete = window.confirm(
      `Do you want to DEACTIVATE "${user.name}"?`
    );
    if (!confirmDelete) return;

    try {
      await update(ref(db, "users/" + user.firebaseKey), {
        status: "inactive",
        inactiveDate: new Date().toISOString().split("T")[0],
      });

      alert("🚫 User Deactivated!");
      if (selectedUser === user.name) setSelectedUser("");
    } catch (error) {
      console.error("Deactivate failed:", error);
      alert("❌ Failed to deactivate user");
    }
  };

  const handleActivateUser = async (user) => {
    const ok = window.confirm(`Activate "${user.name}" again?`);
    if (!ok) return;

    try {
      await update(ref(db, "users/" + user.firebaseKey), {
        status: "active",
      });

      alert("✅ User Activated!");
    } catch (error) {
      console.error(error);
      alert("❌ Failed to activate user");
    }
  };

  const currentUser = users.find((u) => u.name === selectedUser);

  // Mobile responsive styles
  const mobileStyles = {
    sidebar: {
      width: isMobile ? "75vw" : 300,
      padding: isMobile ? "12px" : "18px",
    },
    button: {
      padding: isMobile ? "10px 8px" : "15px",
      fontSize: isMobile ? "12px" : "1rem",
      borderRadius: isMobile ? 8 : 12,
      gap: isMobile ? "6px" : "10px",
    },
    icon: {
      fontSize: isMobile ? "16px" : "26px",
    },
    header: {
      fontSize: isMobile ? "16px" : "20px",
      marginBottom: isMobile ? 8 : 12,
    },
    dropdown: {
      padding: isMobile ? "10px" : "12px",
      fontSize: isMobile ? "13px" : "18px",
    },
    card: {
      padding: isMobile ? "10px" : "14px",
      marginTop: isMobile ? 12 : 18,
      fontSize: isMobile ? "12px" : "14px",
    },
  };

  return (
    <div style={{ display: "flex" }}>
      {/* 🔘 Mobile Toggle Button */}
      {isMobile && (
        <>
          <div
            style={{
              position: "fixed",
              top: topbarHeight + 20,
              left: 12,
              width: 36,
              height: 36,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              background: "linear-gradient(120deg, #03b8b3, #04d4cd)",
              color: "#fff",
              cursor: "pointer",
              zIndex: 30,
              boxShadow: "0 8px 18px rgba(3,184,179,0.35)",
              transform: sidebarOpen ? "translateX(255px)" : "translateX(0)",
              transition: "transform 0.28s ease",
            }}
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <ArrowForwardIosIcon
              style={{
                fontSize: isMobile ? "16px" : "inherit",
                transform: sidebarOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: ".25s",
              }}
            />
          </div>

          {/* Dark Overlay */}
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.25)",
              backdropFilter: "blur(1px)",
              zIndex: 15,
              opacity: sidebarOpen ? 1 : 0,
              pointerEvents: sidebarOpen ? "auto" : "none",
              transition: "opacity 0.25s ease",
            }}
            onClick={() => setSidebarOpen(false)}
          />
        </>
      )}

      {/* 🧱 SIDEBAR */}
      <aside
        style={{
          width: mobileStyles.sidebar.width,
          background: "rgba(255,255,255,0.9)",
          backdropFilter: "blur(8px)",
          borderRight: "1px solid rgba(0,0,0,0.08)",
          boxShadow: "2px 0 18px rgba(0,0,0,0.06)",
          position: isMobile ? "fixed" : "relative",
          height: isMobile ? "100vh" : "auto",
          overflowY: isMobile ? (selectedUser ? "auto" : "hidden") : "auto",
          overflowX: "hidden",
          zIndex: 20,
          transform: isMobile
            ? sidebarOpen
              ? "translateX(0)"
              : "translateX(-105%)"
            : "translateX(0)",
          transition: "transform 0.28s ease",
          paddingBottom: isMobile ? "120px" : "0",
          scrollBehavior: "smooth",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <div
          style={{
            padding: mobileStyles.sidebar.padding,
            height: "auto",
            // minHeight: "100%",
          }}
        >
          <h3
            style={{
              color: "#022c32",
              marginBottom: mobileStyles.header.marginBottom,
              fontSize: mobileStyles.header.fontSize,
            }}
          >
            Customers
          </h3>

          {/* 🔽 Dropdown with Search Inside */}
          <div
            style={{
              position: "relative",
              marginBottom: isMobile ? 12 : 15,
            }}
          >
            {/* Dropdown Header */}
            <div
              onClick={() => setDropdownOpen(!dropdownOpen)}
              style={{
                padding: mobileStyles.dropdown.padding,
                borderRadius: 10,
                border: "1px solid #bcd6db",
                background: "#fff",
                fontWeight: 600,
                color: "#022c32",
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                boxShadow: "0 2px 5px rgba(0,0,0,0.06)",
                fontSize: mobileStyles.dropdown.fontSize,
              }}
            >
              <span>
                {selectedUser ? `👤 ${selectedUser}` : "— Select User —"}
              </span>
              {dropdownOpen ? (
                <KeyboardArrowUpIcon
                  style={{
                    color: "#022c32",
                    fontSize: isMobile ? 22 : 26,
                    transition: "0.2s",
                  }}
                />
              ) : (
                <KeyboardArrowDownIcon
                  style={{
                    color: "#022c32",
                    fontSize: isMobile ? 22 : 26,
                    transition: "0.2s",
                  }}
                />
              )}
            </div>

            {/* Dropdown Menu */}
            {dropdownOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "110%",
                  left: 0,
                  right: 0,
                  background: "#fff",
                  borderRadius: 10,
                  border: "1px solid #bcd6db",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
                  zIndex: 50,
                  maxHeight: isMobile ? 200 : 260,
                  overflowY: "auto",
                  marginRight: "auto",
                  marginLeft: "auto",
                  transition: "all 0.2s ease-in-out",
                }}
              >
                {/* Search Field */}
                <div
                  style={{
                    padding: isMobile ? 6 : 8,
                    borderBottom: "1px solid #e5e5e5",
                    background: "#f8fcfc",
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                  }}
                >
                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      background: "#fff",
                      borderRadius: 8,
                      border: "1px solid #bcd6db",
                      padding: isMobile ? "4px 8px" : "6px 10px",
                      width: "90%",
                      margin: "0 auto",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                    }}
                  >
                    <SearchIcon
                      style={{
                        color: "#022c32",
                        fontSize: isMobile ? 18 : 20,
                        marginRight: 6,
                        flexShrink: 0,
                      }}
                    />
                    <input
                      type="text"
                      placeholder="Search by Name or ID..."
                      value={searchTerm}
                      onChange={(e) =>
                        setSearchTerm(e.target.value.toLowerCase())
                      }
                      style={{
                        flex: 1,
                        border: "none",
                        outline: "none",
                        fontWeight: 500,
                        fontSize: isMobile ? 12 : 14,
                        background: "transparent",
                        color: "#022c32",
                      }}
                    />
                  </div>
                </div>

                {/* Filtered Users List */}
                {users
                  .filter(
                    (u) =>
                      u.name.toLowerCase().includes(searchTerm) ||
                      (u.userId && u.userId.toLowerCase().includes(searchTerm))
                  )
                  .map((u) => (
                    <div
                      key={u.firebaseKey}
                      onClick={() => {
                        setSelectedUser(u.name);
                        setShowAll(false);
                        setDropdownOpen(false);
                        setShowBookings(false);
                        setShowSales(false);
                        setShowEmployees(false);
                        if (isMobile) setSidebarOpen(false);
                        setShowInactive(false);
                        setShowDaily(false);
                        setShowAttendanceUI(false);
                        setShowAttendanceReportUI(false);
                        setShowPaymentRequests(false);
                      }}
                      style={{
                        padding: isMobile ? "8px 10px" : "10px 12px",
                        borderBottom: "1px solid #f2f2f2",
                        cursor: "pointer",
                        fontWeight: 500,
                        color: "#022c32",
                        transition: "0.2s",
                        fontSize: isMobile ? "12px" : "inherit",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "#f1f8f8")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      {u.name}
                      <span
                        style={{
                          color: "#888",
                          fontSize: isMobile ? "0.75rem" : "0.85rem",
                        }}
                      >
                        — {u.userId}
                      </span>
                    </div>
                  ))}

                {/* Empty State */}
                {users.filter(
                  (u) =>
                    u.name.toLowerCase().includes(searchTerm) ||
                    (u.userId && u.userId.toLowerCase().includes(searchTerm))
                ).length === 0 && (
                  <div
                    style={{
                      textAlign: "center",
                      padding: isMobile ? "10px" : "12px",
                      color: "#888",
                      fontSize: isMobile ? 12 : 14,
                    }}
                  >
                    No users found
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 🏠 Dashboard Button */}
          <button
            onClick={() => {
              setSelectedUser("");
              setShowAll(false);
              setShowSales(false);
              setShowBookings(false);
              setShowInactive(false);
              setShowDaily(false);
              setShowEmployees(false);
              if (isMobile) setSidebarOpen(false);
              setShowAttendanceUI(false);
              setShowAttendanceReportUI(false);
              setShowPaymentRequests(false);
            }}
            style={{
              width: "100%",
              padding: mobileStyles.button.padding,
              fontSize: mobileStyles.button.fontSize,
              borderRadius: mobileStyles.button.borderRadius,
              background: "linear-gradient(120deg, #2196F3, #64B5F6)",
              border: "none",
              color: "#fff",
              fontWeight: 600,
              cursor: "pointer",
              marginBottom: isMobile ? 8 : 12,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: mobileStyles.button.gap,
            }}
          >
            <HomeIcon
              style={{
                fontSize: mobileStyles.icon.fontSize,
                filter: "drop-shadow(1px 1px 1px rgba(0,0,0,0.5))",
                transform: "scale(1.1)",
              }}
            />
            Dashboard
          </button>

          {/* ➕ Add New Customer */}
          <button
            onClick={() => setShowModal(true)}
            style={{
              width: "100%",
              padding: mobileStyles.button.padding,
              fontSize: mobileStyles.button.fontSize,
              borderRadius: mobileStyles.button.borderRadius,
              background: "linear-gradient(120deg, #03b8b3, #04d4cd)",
              border: "none",
              color: "#fff",
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: mobileStyles.button.gap,
            }}
          >
            <PersonAddIcon
              style={{
                fontSize: mobileStyles.icon.fontSize,
                filter: "drop-shadow(1px 1px 1px rgba(0,0,0,0.5))",
                transform: "scale(1.1)",
              }}
            />
            Add New Customer
          </button>

          {/* 👥 View All Customers */}
          <button
            onClick={() => {
              setSelectedUser("");
              setShowSales(false);
              if (setShowAll) setShowAll(true);
              if (isMobile) setSidebarOpen(false);
              setShowInactive(false);
              setShowDaily(false);
              setShowEmployees(false);
              setShowAttendanceUI(false);
              setShowAttendanceReportUI(false);
              setShowPaymentRequests(false);
            }}
            style={{
              width: "100%",
              padding: mobileStyles.button.padding,
              fontSize: mobileStyles.button.fontSize,
              borderRadius: mobileStyles.button.borderRadius,
              background: "linear-gradient(120deg, #04b870, #02d18f)",
              border: "none",
              color: "#fff",
              fontWeight: 600,
              cursor: "pointer",
              marginTop: isMobile ? 8 : 12,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: mobileStyles.button.gap,
            }}
          >
            <PeopleAltIcon
              style={{
                fontSize: mobileStyles.icon.fontSize,
                filter: "drop-shadow(1px 1px 1px rgba(0,0,0,0.5))",
                transform: "scale(1.1)",
              }}
            />
            View All Customers
          </button>

          {/* 🧾 View All Sales Button */}
          <button
            onClick={() => {
              setSelectedUser("");
              if (setShowAll) setShowAll(false);
              setShowSales(true);
              window.dispatchEvent(new Event("showSalesTable"));
              if (isMobile) setSidebarOpen(false);
              setShowInactive(false);
              setShowDaily(false);
              setShowEmployees(false);
              setShowAttendanceUI(false);
              setShowAttendanceReportUI(false);
              setShowPaymentRequests(false);
            }}
            style={{
              width: "100%",
              padding: mobileStyles.button.padding,
              fontSize: mobileStyles.button.fontSize,
              borderRadius: mobileStyles.button.borderRadius,
              background: "linear-gradient(120deg, #f57c00, #ffb74d)",
              border: "none",
              color: "#fff",
              fontWeight: 600,
              cursor: "pointer",
              marginTop: isMobile ? 8 : 12,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: mobileStyles.button.gap,
            }}
          >
            <CurrencyRupeeIcon
              style={{
                fontSize: mobileStyles.icon.fontSize,
                filter: "drop-shadow(1px 1px 1px rgba(0,0,0,0.5))",
                transform: "scale(1.1)",
              }}
            />
            View All Sales
          </button>

          {/* 🧾 View Todays Sales Button */}
          <button
            onClick={() => {
              setShowDaily(true);
              setShowAll(false);
              setShowSales(false);
              setShowBookings(false);
              setShowInactive(false);
              setSelectedUser("");
              setShowEmployees(false);
              setShowAttendanceUI(false);
              setShowAttendanceReportUI(false);
              if (isMobile) setSidebarOpen(false);
              setShowPaymentRequests(false);
            }}
            style={{
              width: "100%",
              padding: mobileStyles.button.padding,
              fontSize: mobileStyles.button.fontSize,
              borderRadius: mobileStyles.button.borderRadius,
              background: "linear-gradient(135deg, #d4af37, #f7d774)",
              border: "none",
              color: "#fff",
              fontWeight: 600,
              cursor: "pointer",
              marginTop: isMobile ? 8 : 12,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: mobileStyles.button.gap,
            }}
          >
            <CurrencyRupeeIcon
              style={{
                fontSize: mobileStyles.icon.fontSize,
                filter: "drop-shadow(1px 1px 1px rgba(121, 99, 99, 0.5))",
                transform: "scale(1.1)",
              }}
            />
            Daily Sales
          </button>

          {/* 🆕 View Bookings Button */}
          <div
            style={{
              position: "relative",
              display: "inline-block",
              width: "100%",
            }}
          >
            <button
              onClick={() => {
                if (setShowAll) setShowAll(false);
                setShowSales(false);
                setShowEmployees(false);
                window.dispatchEvent(new Event("showBookingsTable"));
                if (isMobile) setSidebarOpen(false);
                setShowInactive(false);
                setShowDaily(false);
                setShowAttendanceUI(false);
                setShowAttendanceReportUI(false);
                setSelectedUser("");
                setShowPaymentRequests(false);
              }}
              style={{
                width: "100%",
                padding: mobileStyles.button.padding,
                fontSize: mobileStyles.button.fontSize,
                borderRadius: mobileStyles.button.borderRadius,
                background: "linear-gradient(120deg, #3f51b5, #5c6bc0)",
                border: "none",
                color: "#fff",
                fontWeight: 600,
                cursor: "pointer",
                marginTop: isMobile ? 8 : 12,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: mobileStyles.button.gap,
                position: "relative",
              }}
              aria-label={`View bookings${
                todayBookingCount > 0 ? `, ${todayBookingCount} for today` : ""
              }`}
            >
              <AddIcon
                style={{
                  fontSize: mobileStyles.icon.fontSize,
                  filter: "drop-shadow(1px 1px 1px rgba(0,0,0,0.5))",
                  transform: "scale(1.1)",
                }}
              />
              View Bookings
            </button>

            {/* Notification badge */}
            {todayBookingCount > 0 && (
              <span
                style={{
                  position: "absolute",
                  top: isMobile ? 15 : 20,
                  right: 0,
                  background: "#e53935",
                  color: "#fff",
                  fontSize: isMobile ? 16 : 20,
                  padding: isMobile ? "2px 5px" : "4px 7px",
                  borderRadius: 999,
                  fontWeight: 800,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                  lineHeight: 1,
                  transform: "translateY(-50%)",
                  minWidth: isMobile ? "18px" : "auto",
                  textAlign: "center",
                }}
              >
                {todayBookingCount}
              </span>
            )}
          </div>

          {/* Inactive button */}
          <button
            onClick={() => {
              setShowInactive(true);
              setShowAll(false);
              setShowSales(false);
              setShowBookings(false);
              setShowEmployees(false);
              setSelectedUser("");
              if (isMobile) setSidebarOpen(false);
              setShowDaily(false);
              setShowAttendanceUI(false);
              setShowAttendanceReportUI(false);
              setShowPaymentRequests(false);
            }}
            style={{
              width: "100%",
              padding: mobileStyles.button.padding,
              fontSize: mobileStyles.button.fontSize,
              borderRadius: mobileStyles.button.borderRadius,
              background: "linear-gradient(120deg, #c62828, #ef5350)",
              border: "none",
              color: "#fff",
              fontWeight: 600,
              cursor: "pointer",
              marginTop: isMobile ? 8 : 12,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: mobileStyles.button.gap,
            }}
          >
            <NoAccountsIcon
              style={{
                fontSize: mobileStyles.icon.fontSize,
                filter: "drop-shadow(1px 1px 1px rgba(0,0,0,0.5))",
                transform: "scale(1.1)",
              }}
            />
            Inactive Customers
          </button>

          {/* Manage Employees Button */}
          <button
            onClick={() => {
              setShowEmployees(true);
              setShowInactive(false);
              setShowAll(false);
              setShowSales(false);
              setShowBookings(false);
              setShowAttendanceUI(false);
              setShowAttendanceReportUI(false);
              setSelectedUser("");
              setShowDaily(false);
              if (isMobile) setSidebarOpen(false);
              setShowPaymentRequests(false);
            }}
            style={{
              width: "100%",
              padding: mobileStyles.button.padding,
              fontSize: mobileStyles.button.fontSize,
              borderRadius: mobileStyles.button.borderRadius,
              background: "linear-gradient(135deg, #0F2027, #203A43, #2C5364)",
              border: "none",
              color: "#fff",
              fontWeight: 600,
              cursor: "pointer",
              marginTop: isMobile ? 8 : 12,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: mobileStyles.button.gap,
            }}
          >
            <Groups2Icon
              style={{
                fontSize: mobileStyles.icon.fontSize,
                filter: "drop-shadow(1px 1px 1px rgba(0,0,0,0.5))",
                transform: "scale(1.1)",
              }}
            />
            Manage Employees
          </button>

          {/* 💳 Payment Requests */}
          <button
            onClick={() => {
              setShowPaymentRequests(true);

              // ⬇️ sab aur screens band
              setShowAll(false);
              setShowSales(false);
              setShowBookings(false);
              setShowInactive(false);
              setShowDaily(false);
              setShowEmployees(false);
              setShowAttendanceUI(false);
              setShowAttendanceReportUI(false);

              setSelectedUser("");

              if (isMobile) setSidebarOpen(false);
            }}
            style={{
              width: "100%",
              padding: mobileStyles.button.padding,
              fontSize: mobileStyles.button.fontSize,
              borderRadius: mobileStyles.button.borderRadius,
              background: "linear-gradient(120deg, #7B1FA2, #BA68C8)",
              border: "none",
              color: "#fff",
              fontWeight: 600,
              cursor: "pointer",
              marginTop: isMobile ? 8 : 12,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: mobileStyles.button.gap,
            }}
          >
            <MdPendingActions
              style={{
                fontSize: mobileStyles.icon.fontSize,
                filter: "drop-shadow(1px 1px 1px rgba(0,0,0,0.5))",
                transform: "scale(1.1)",
              }}
            />
            Payment Requests
          </button>

          {/* 💳 Payment Requests */}
          <button
            onClick={() => setShowUpiModal(true)}
            style={{
              width: "100%",
              padding: mobileStyles.button.padding,
              fontSize: mobileStyles.button.fontSize,
              borderRadius: mobileStyles.button.borderRadius,
              background: "linear-gradient(120deg, #ba3a8dff, #6f317aff)",
              border: "none",
              color: "#fff",
              fontWeight: 600,
              cursor: "pointer",
              marginTop: isMobile ? 8 : 12,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: mobileStyles.button.gap,
            }}
          >
            <HiSwitchHorizontal
              style={{
                fontSize: mobileStyles.icon.fontSize,
                filter: "drop-shadow(1px 1px 1px rgba(0,0,0,0.5))",
                transform: "scale(1.1)",
              }}
            />
            Update UPI
          </button>

          {/* 🧾 Info Card */}
          {currentUser && (
            <div
              style={{
                marginTop: mobileStyles.card.marginTop,
                padding: mobileStyles.card.padding,
                background: "#fff",
                borderRadius: 10,
                boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                border: "1px solid #d5eaea",
                maxWidth: "100%",
                wordBreak: "break-word",
                overflow: "visible",
                marginBottom: isMobile ? "130px" : "65px",
                fontSize: mobileStyles.card.fontSize,
                lineHeight: "20px",
              }}
            >
              <h4
                style={{
                  color: "#022c32",
                  marginBottom: 8,
                  fontSize: isMobile ? "14px" : "inherit",
                }}
              >
                {currentUser.name}
              </h4>

              <p style={{ margin: "4px 0", fontSize: "inherit" }}>
                <b>User ID:</b> {currentUser.userId}
              </p>
              <p style={{ margin: "4px 0", fontSize: "inherit" }}>
                <b>Mobile:</b> {currentUser.contact}
              </p>
              <p style={{ margin: "4px 0", fontSize: "inherit" }}>
                <b>Address:</b> {currentUser.address}
              </p>
              <p style={{ margin: "4px 0", fontSize: "inherit" }}>
                <b>Capsule Price:</b> ₹{currentUser.capsulePrice}
              </p>
              <p style={{ margin: "4px 0", fontSize: "inherit" }}>
                <b>Jar Price:</b> ₹{currentUser.jarPrice}
              </p>

              {/* 🗓️ New: Joining Date */}
              {currentUser.joiningDate && (
                <p style={{ margin: "4px 0", fontSize: "inherit" }}>
                  <b>Joining Date:</b>{" "}
                  {new Date(currentUser.joiningDate).toLocaleDateString(
                    "en-IN",
                    {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    }
                  )}
                </p>
              )}

              {/* 🔑 New: Password */}
              {currentUser.password && (
                <p style={{ margin: "4px 0", fontSize: "inherit" }}>
                  <b>Password:</b> {currentUser.password}
                </p>
              )}

              <div style={{ display: "flex", gap: "6px", marginTop: "8px" }}>
                <button
                  style={{
                    flex: 1,
                    padding: isMobile ? "6px 8px" : "8px 12px",
                    borderRadius: 6,
                    background: "linear-gradient(120deg, #ff9800, #ffb74d)",
                    border: "none",
                    color: "#fff",
                    fontWeight: 600,
                    cursor: "pointer",
                    fontSize: isMobile ? "12px" : "14px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "4px",
                  }}
                  onClick={() => {
                    setEditUser(currentUser);
                    setShowEditModal(true);
                  }}
                >
                  <EditIcon style={{ fontSize: isMobile ? "16px" : "18px" }} />
                </button>

                <button
                  style={{
                    flex: 1,
                    padding: isMobile ? "6px 8px" : "8px 12px",
                    borderRadius: 6,
                    background: "linear-gradient(120deg, #e53935, #ff5252)",
                    border: "none",
                    color: "#fff",
                    fontWeight: 600,
                    cursor: "pointer",
                    fontSize: isMobile ? "12px" : "14px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "4px",
                  }}
                  onClick={() => handleDeleteUser(currentUser)}
                >
                  <NoAccountsIcon
                    style={{ fontSize: isMobile ? "16px" : "18px" }}
                  />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* 🔴 Inactive Users List */}
        {inactiveUsers.length > 0 && (
          <div
            style={{
              marginTop: isMobile ? 15 : 20,
              padding: mobileStyles.sidebar.padding,
            }}
          >
            <h3
              style={{
                color: "#b30000",
                fontSize: isMobile ? "14px" : "inherit",
              }}
            >
              Inactive Customers
            </h3>

            {inactiveUsers.map((u) => (
              <div
                key={u.firebaseKey}
                style={{
                  padding: isMobile ? "8px" : "10px",
                  marginBottom: "6px",
                  borderRadius: "8px",
                  background: "#ffe8e8",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  fontSize: isMobile ? "12px" : "inherit",
                }}
              >
                <span>
                  {u.name} — {u.userId}
                </span>

                <button
                  onClick={() => handleActivateUser(u)}
                  style={{
                    background: "linear-gradient(120deg, #03b8b3, #04d4cd)",
                    border: "none",
                    color: "#fff",
                    padding: isMobile ? "4px 8px" : "6px 12px",
                    borderRadius: 6,
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: isMobile ? "11px" : "12px",
                  }}
                >
                  Activate
                </button>
              </div>
            ))}
          </div>
        )}
      </aside>

      {showUpiModal && (
        <div
          onClick={closeUpiModal}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            animation: isClosing
              ? "fadeOut 0.25s ease forwards"
              : "fadeIn 0.25s ease",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 14,
              padding: 22,
              width: "80%",
              maxWidth: 360,
              textAlign: "center",
              boxShadow: "0 12px 30px rgba(0,0,0,0.25)",
              animation: isClosing
                ? "popOut 0.25s ease forwards"
                : "popIn 0.3s cubic-bezier(0.25,0.8,0.3,1)",
            }}
          >
            <h3 style={{ marginBottom: 12, color: "#022c32" }}>
              💳 Update UPI ID
            </h3>

            <input
              type="text"
              placeholder="example@upi"
              value={upiId}
              onChange={(e) => setUpiId(e.target.value)}
              style={{
                width: "90%",
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid #ccc",
                outline: "none",
                marginBottom: 14,
                fontSize: 14,
              }}
            />

            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={closeUpiModal}
                style={{
                  flex: 1,
                  padding: "10px",
                  borderRadius: 8,
                  border: "none",
                  background: "#eee",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>

              <button
                onClick={saveUpiId}
                disabled={savingUpi}
                style={{
                  flex: 1,
                  padding: "10px",
                  borderRadius: 8,
                  border: "none",
                  background: "linear-gradient(120deg, #ba3a8d, #6f317a)",
                  color: "#fff",
                  fontWeight: 600,
                  cursor: "pointer",
                  opacity: savingUpi ? 0.7 : 1,
                }}
              >
                {savingUpi ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ➕ Add Modal */}
      {showModal && (
        <ModalWrapper
          title="Add New Customer"
          fields={newUser}
          setFields={setNewUser}
          onSubmit={handleAddUser}
          onClose={() => setShowModal(false)}
          buttonLabel="Save Customer"
          nextId={nextId}
          loading={loading}
          isMobile={isMobile}
        />
      )}

      {/* ✏️ Edit Modal */}
      {showEditModal && editUser && (
        <ModalWrapper
          title="Edit Customer Details"
          fields={editUser}
          setFields={setEditUser}
          onSubmit={handleEditSave}
          onClose={() => setShowEditModal(false)}
          buttonLabel="Save Changes"
          loading={loading}
          isMobile={isMobile}
        />
      )}
    </div>
  );
};

// ♻️ Modal Component
function ModalWrapper({
  title,
  fields,
  setFields,
  onSubmit,
  onClose,
  buttonLabel,
  nextId,
  loading,
  isMobile,
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 100,
      }}
    >
      <div
        style={{
          background: "#fff",
          padding: isMobile ? 16 : 22,
          borderRadius: 12,
          width: "100%",
          maxWidth: isMobile ? 280 : 360,
          boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
          marginTop: isMobile ? "60px" : "0",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <h3
          style={{
            textAlign: "center",
            color: "#022c32",
            fontSize: isMobile ? "16px" : "inherit",
            marginBottom: isMobile ? 8 : 12,
          }}
        >
          {title}
        </h3>
        {nextId && (
          <div
            style={{
              textAlign: "center",
              fontWeight: 600,
              color: "#03b8b3",
              marginBottom: isMobile ? 8 : 10,
              fontSize: isMobile ? "13px" : "inherit",
            }}
          >
            Customer ID: {nextId}
          </div>
        )}

        {[
          "name",
          "contact",
          "address",
          "capsulePrice",
          "jarPrice",
          "password",
          "joiningDate",
        ].map((f) => (
          <input
            key={f}
            type={
              f.includes("Price")
                ? "number"
                : f === "password"
                ? "password"
                : f === "joiningDate"
                ? "date"
                : "text"
            }
            placeholder={
              f === "name"
                ? "Customer Name"
                : f === "contact"
                ? "Mobile Number"
                : f === "address"
                ? "Address"
                : f === "capsulePrice"
                ? "Capsule Price (₹)"
                : f === "jarPrice"
                ? "Jar Price (₹)"
                : f === "password"
                ? "Set Password"
                : "Joining Date"
            }
            value={fields[f] || ""}
            onChange={(e) => setFields({ ...fields, [f]: e.target.value })}
            style={{
              width: "97%",
              padding: isMobile ? "6px 8px" : "8px",
              marginBottom: isMobile ? 4 : 6,
              borderRadius: 8,
              border: "1px solid #bcd6db",
              outline: "none",
              fontSize: isMobile ? "12px" : "14px",
            }}
          />
        ))}

        <button
          onClick={onSubmit}
          disabled={loading}
          style={{
            width: "100%",
            padding: isMobile ? "10px 8px" : "12px",
            fontSize: isMobile ? "13px" : "1rem",
            borderRadius: isMobile ? 8 : 12,
            background: "linear-gradient(120deg, #03b8b3, #04d4cd)",
            border: "none",
            color: "#fff",
            fontWeight: 600,
            cursor: loading ? "not-allowed" : "pointer",
            opacity: loading ? 0.6 : 1,
            marginTop: isMobile ? 8 : 12,
          }}
        >
          {loading ? "Saving..." : buttonLabel}
        </button>

        <p
          onClick={onClose}
          style={{
            textAlign: "center",
            marginTop: isMobile ? 8 : 10,
            color: "#03b8b3",
            cursor: "pointer",
            fontWeight: 600,
            fontSize: isMobile ? "13px" : "inherit",
          }}
        >
          Cancel
        </p>
      </div>
    </div>
  );
}

export default Sidebar;